import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Upload,
  DatePicker,
  Image,
  notification,
  message,
  Space,
  Row,
  Col,
  Popconfirm,
  Tabs,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

import {
  timeZoneOptions,
  tagsOptions,
  countryOptions,
  cityOptions,
} from "utility/options/index";

import { convertTimeStamp } from "utility/functions/convertTimestamp";

import { useNavigate } from "react-router-dom";
import cities from "utility/options/taiwan_all_county.json";

import axios from "axios";
import {
  apiGetEventList,
  apiUpdateEventStatus,
  apiCopyEvent,
  apiDeleteEvent,
  apiCheckEventCode,
} from "api/api";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const eventStatusTabs = [
  { label: "全部", key: "0", children: null },
  { label: "草稿", key: "1", children: null },
  { label: "上架", key: "2", children: null },
  { label: "結束", key: "4", children: null },
];

const Events = () => {
  // # react router dom
  const navigate = useNavigate();

  // # Form instance
  const [form] = Form.useForm();

  // # Antd
  const { RangePicker } = DatePicker;
  const [api, contextHolder] = notification.useNotification();
  const requireMsg = "此欄位必填!";
  const isEmptyValue = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  const addressFieldNames = ["country", "city", "line", "remark"];
  const requireIfAnyAddressFieldFilled = (label) => ({
    validator: async (_, value) => {
      const values = form.getFieldsValue(addressFieldNames);
      const shouldValidate = addressFieldNames.some(
        (fieldName) => !isEmptyValue(values[fieldName]),
      );

      if (!shouldValidate || !isEmptyValue(value)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(`${label}${requireMsg}`));
    },
  });

  // # 取得資料
  const [title, setTitle] = useState("");
  const [statusTab, setStatusTab] = useState("0");
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(undefined);

  const [dataSource, setDataSource] = useState([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await apiGetEventList({
        title,
        status: statusTab === "0" ? undefined : Number(statusTab),
        limit: pageSize,
        offset: current * pageSize - pageSize,
        sortFields: ["CreationDate"],
        sortDirection: 1,
      });
      const { content, paging } = res.data;
      setDataSource([
        ...content.map((item) => ({
          ...item,
          isCopyBtnLoading: false,
          isDeleteProcessing: false,
        })),
      ]);
      setTotal(paging.count);
    } catch (err) {
      message.error(err.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [current, pageSize, statusTab]);

  // # 圖片預覽
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewMobileOpen, setPreviewMobileOpen] = useState();
  const [previewMobileImage, setPreviewMobileImage] = useState();
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewMobileTitle, setPreviewMobileTitle] = useState("");
  const [fileList, setFileList] = useState([]);
  const [mobileFileList, setMobileFileList] = useState([]);

  // * 彈窗
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const showModal = () => {
    setIsModalOpen(true);
  };

  // * 提交表單
  const [isLoading, setIsLoading] = useState(true);

  const onFinish = (values) => {
    console.log("Success:", values);
    handleCreate(values);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const normalizeUpload = (event) => {
    if (Array.isArray(event)) return event;
    return event?.fileList;
  };

  const getUploadFile = (value, fallbackList = []) => {
    const list = Array.isArray(value)
      ? value
      : Array.isArray(value?.fileList)
        ? value.fileList
        : fallbackList;

    return list?.[0]?.originFileObj || list?.[0]?.file || null;
  };

  const handleCreate = async (values) => {
    const token = sessionStorage.t;
    let bodyFormData = new FormData();
    const {
      title,
      code,
      tags,
      country,
      city,
      line,
      remark,
      dateTime,
      socialLinks,
      files,
      link,
      socialLinkTitle,
      webCover,
      mobileCover,
      // MobileFiles,
    } = values;
    const socialLinkUrl = socialLinks?.url;
    const appendIfDefined = (key, value) => {
      if (value !== undefined && value !== null && value !== "") {
        bodyFormData.append(key, value);
      }
    };
    const webCoverFile = getUploadFile(webCover, fileList);
    const mobileCoverFile = getUploadFile(mobileCover, mobileFileList);

    appendIfDefined("title", title);
    if (code) {
      bodyFormData.append("code", code);
    }
    (Array.isArray(tags) ? tags : tags ? [tags] : []).forEach((tag) => {
      bodyFormData.append("tags", tag);
    });
    appendIfDefined("address.country", country);
    appendIfDefined("address.city", city);
    appendIfDefined("address.line", line);
    appendIfDefined("address.remark", remark);
    appendIfDefined("address.link", link);
    appendIfDefined("Files", JSON.stringify(files));
    appendIfDefined("socialLink.title", socialLinkTitle);
    appendIfDefined("socialLink.url", socialLinkUrl);
    if (webCoverFile) {
      bodyFormData.append("webCover", webCoverFile);
    }
    if (mobileCoverFile) {
      bodyFormData.append("mobileCover", mobileCoverFile);
    }
    appendIfDefined("startDateTime", dayjs(dateTime[0]?.$d)?.unix());
    appendIfDefined("endDateTime", dayjs(dateTime[1]?.$d)?.unix());
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/Activity`,
        bodyFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      api.success({
        message: `新增成功`,
        // description: `${res.message}`,
      });
      fetchData();
      setTimeout(() => {
        setIsModalOpen(false);
      }, 500);
    } catch (err) {
      console.log("失敗", err.response);
      api.error({
        message: `新增失敗`,
        description: "",
      });
      setTimeout(() => {
        fetchData();
      }, 500);

      console.log("失敗：：", err);
    }
  };

  // # 表格變化
  const onTableChange = (pagination) => {
    setCurrent(pagination.current);
  };

  // # 變更每頁顯示數量
  const onShowSizeChange = (current, pageSize) => {
    setPageSize(pageSize);
  };

  // # 切換狀態分頁
  const handleStatusTabChange = (key) => {
    setCurrent(1);
    setStatusTab(key);
  };

  // # 改變datasource 中的loading 狀態
  const changeLoading = (id, key, isLoading) => {
    setDataSource([
      ...dataSource.map((item) =>
        item.id === id ? { ...item, [key]: isLoading } : item,
      ),
    ]);
  };

  // # 列表刪除
  const handleDelete = async (record) => {
    changeLoading(record.id, "isDeleteProcessing", true);
    try {
      const res = await apiDeleteEvent({ id: record?.id });
      message.success("刪除成功");
      // 如果刪除前只剩一筆資料，並且成功，則回到上一頁
      if (dataSource.length === 1) {
        setCurrent(current - 1);
      }
    } catch (error) {
      message.error(error.response.data.message);
    } finally {
      fetchData();
      changeLoading(record.id, "isDeleteProcessing", false);
    }
  };

  // # 複製活動
  const handleCopy = async ({ id }) => {
    const changeGoal = dataSource.find((item) => item.id === id);
    changeGoal.isCopyBtnLoading = true;
    changeLoading(id, "isCopyBtnLoading", true);
    try {
      const res = await apiCopyEvent({ id });
      message.success("複製成功");
    } catch (error) {
      message.error(error.response.data.message);
    } finally {
      fetchData();
      changeLoading(id, "isCopyBtnLoading", false);
    }
  };

  const handleDetail = (record) => {
    sessionStorage.target_id = record.id;
    navigate(`/basicInfo/${record.id}`); // 點擊行
  };

  // # row-click
  const rowClick = (record) => {
    return {
      onClick: (event) => {
        navigate(`/basicInfo/${record.id}`); // 點擊行
        sessionStorage.target_id = record.id;
      },
    };
  };

  // # 變更狀態
  const handleStatusChange = async (value, record) => {
    try {
      await apiUpdateEventStatus({
        id: record.id,
        status: value,
      });
      fetchData();
      message.success("狀態已變更");
    } catch (error) {
      message.error(error?.response?.data?.message || "變更失敗");
    }
  };

  const columns = [
    {
      title: "狀態",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Select
          value={record.status}
          options={[
            { label: "草稿", value: 1 },
            { label: "上架", value: 2 },
            { label: "結束", value: 4 },
          ]}
          onChange={(value) => handleStatusChange(value, record)}
          style={{ width: 100 }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "封面",
      dataIndex: "cover",
      key: "cover",
      render: (text) =>
        text ? (
          <Image
            src={text}
            width={80}
            height={50}
            style={{ objectFit: "cover" }}
          />
        ) : (
          <span className="text-gray-400 text-xs">無封面</span>
        ),
    },
    {
      title: "活動名稱",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "活動時間",
      dataIndex: "startDateTime",
      key: "startDateTime",
      render: (text, record) => (
        <p>
          {convertTimeStamp(text)} ～ {convertTimeStamp(record.endDateTime)}
        </p>
      ),
    },
    {
      title: "活動地點",
      dataIndex: "address",
      key: "address",
      render: (text) => (
        <p>
          {text?.country}
          {text?.city}
          {text?.line}
        </p>
      ),
    },
    {
      title: "操作",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleDetail(record)} type="primary">
            詳情
          </Button>
          <Button
            type="primary"
            loading={record?.isCopyBtnLoading}
            onClick={() => handleCopy({ id: record.id })}
            className="bg-black"
          >
            複製
          </Button>
          <Popconfirm
            title="刪除該筆活動"
            description="是否確定要刪除該活動？"
            onConfirm={() => handleDelete(record)}
            okText="是的"
            cancelText="取消"
          >
            <Button loading={record?.isDeleteProcessing} type="primary" danger>
              刪除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // # 封面圖片更新時
  const coverProps = {
    accept: ".gif,.jpg,.png",
    name: "image",
    withCredentials: true,
    listType: "picture-card",
    beforeUpload: (file) => {
      return false;
    },
    onChange({ file, fileList: newFileList }) {
      setFileList(newFileList);
    },
  };

  // # 手機版封面圖片更新時
  const mobileCoverProps = {
    accept: ".gif,.jpg,.png",
    name: "image",
    withCredentials: true,
    listType: "picture-card",
    beforeUpload: (file) => {
      return false;
    },
    onChange({ file, fileList: newFileList }) {
      setMobileFileList(newFileList);
    },
  };

  // # 圖片預覽
  const handlePreview = async (file) => {
    // if (!file.url && !file.preview) {
    //   file.preview = await getBase64(file.originFileObj);
    // }
    console.log("file", file);
    setPreviewImage(file.url || file.preview || file.thumbUrl);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1),
    );
  };

  // # 手機圖片預覽
  const handleMobilePreview = async (file) => {
    console.log("file", file);
    setPreviewMobileImage(file.url || file.preview || file.thumbUrl);
    setPreviewMobileOpen(true);
    setPreviewMobileTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1),
    );
  };

  // # 城市選項
  useEffect(() => {
    cities.map((item) => {
      item.label = item.name;
      item.value = item.name;
      cityOptions.push(item);
    });
  }, [cities]);

  return (
    <>
      {contextHolder}
      <Card
        title={<h3 className="text-left">活動列表</h3>}
        className="text-center mt-5 rounded-3xl px-0 min-h-[calc(100vh-250px)]"
        extra={
          <Button type="primary" onClick={showModal}>
            新增
          </Button>
        }
      >
        <Tabs
          activeKey={statusTab}
          items={eventStatusTabs}
          onChange={handleStatusTabChange}
          className="[&_.ant-tabs-content-holder]:hidden"
        />
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          showHeader={false}
          onChange={onTableChange}
          pagination={{
            total,
            current,
            pageSize,
            showSizeChanger: true,
            onShowSizeChange,
          }}
          // onRow={rowClick}
          className="[&_.ant-table-cell-row-hover]:cursor-pointer"
          scroll={{ x: "max-content" }}
        ></Table>
      </Card>
      <Modal
        title={"新增活動內容"}
        open={isModalOpen}
        footer={null}
        onCancel={handleCancel}
        okText={"建立"}
        cancelText={"取消"}
        destroyOnClose={true}
        width={1100}
      >
        <Form
          name="basic"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
          variant="filled"
          size="large"
          form={form}
          scrollToFirstError={true}
        >
          <Row gutter={16}>
            <Col
              xs={24}
              md={18}
              className="[&_.ant-form-item-control-input-content]:h-[400px] [&_.ant-upload]:h-[400px] [&_.ant-upload-list-item]:h-[400px] mb-6 md:mb-0"
            >
              <Form.Item
                label="桌機封面"
                name="webCover"
                valuePropName="fileList"
                getValueFromEvent={normalizeUpload}
              >
                <Upload
                  {...coverProps}
                  fileList={fileList}
                  onPreview={handlePreview}
                  className="w-full [&_.ant-upload-list-item-container]:grow [&_.ant-upload.ant-upload-select]:grow"
                >
                  {fileList && fileList?.length >= 1 ? null : (
                    <button
                      style={{ border: 0, background: "none" }}
                      type="button"
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>上傳電腦版封面</div>
                    </button>
                  )}
                </Upload>
              </Form.Item>
            </Col>
            <Col
              xs={24}
              md={6}
              className="[&_.ant-form-item-control-input-content]:h-[400px] [&_.ant-upload]:h-[400px] [&_.ant-upload-list-item]:h-[400px]"
            >
              <Form.Item
                label="手機版封面"
                name="mobileCover"
                valuePropName="fileList"
                getValueFromEvent={normalizeUpload}
              >
                <Upload
                  {...mobileCoverProps}
                  fileList={mobileFileList}
                  onPreview={handleMobilePreview}
                  className="w-full [&_.ant-upload-list-item-container]:grow [&_.ant-upload.ant-upload-select]:grow"
                >
                  {mobileFileList?.length >= 1 ? null : (
                    <button
                      style={{ border: 0, background: "none" }}
                      type="button"
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>上傳手機版封面</div>
                    </button>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="活動名稱"
            name="title"
            rules={[
              {
                required: true,
                message: requireMsg,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="客製化網址" required style={{ marginBottom: 0 }}>
            <div className="flex flex-wrap gap-2 items-start">
              <Form.Item
                name="code"
                className="mb-0 flex-1 min-w-0 text-left"
                validateTrigger="onBlur"
                rules={[
                  {
                    required: true,
                    message: requireMsg,
                  },
                  {
                    validator: async (_, value) => {
                      if (!value) return Promise.resolve();
                      try {
                        const res = await apiCheckEventCode({
                          id: 0,
                          code: value,
                        });
                        if (res.data?.content === false) {
                          return Promise.reject(new Error("此網址已重複"));
                        }
                        return Promise.resolve();
                      } catch (err) {
                        return Promise.reject(
                          new Error(
                            err.response?.data?.message ||
                              "驗證失敗，請稍後再試",
                          ),
                        );
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="請輸入客製化網址" />
              </Form.Item>
              <Button
                type="primary"
                onClick={() => {
                  const code = form.getFieldValue("code");
                  if (code) {
                    const host = (
                      process.env.REACT_APP_WEB_DOMAIN || window.location.origin
                    ).replace(/\/+$/, "");
                    const shareUrl = `${host}/activity/${code}`;
                    navigator.clipboard.writeText(shareUrl);
                    api.success({
                      message: "成功複製連結",
                      description: shareUrl,
                    });
                  } else {
                    api.warning({
                      message: "請先輸入客製化網址",
                    });
                  }
                }}
              >
                分享
              </Button>
            </div>
          </Form.Item>
          {/* <Form.Item
            label="時區"
            name="TimeZone"
            rules={[
              {
                required: true,
                message: requireMsg,
              },
            ]}
          >
            <Select options={timeZoneOptions} className="text-left" />
          </Form.Item> */}
          <Form.Item
            label="活動時間 (輸入格式：YYYY-MM-DD HH:mm)"
            name="dateTime"
            rules={[
              {
                type: "array",
                required: true,
                message: requireMsg,
              },
              {
                validator: async (_, value) => {
                  if (!value || value.length !== 2) return Promise.resolve();

                  const [startDateTime, endDateTime] = value;
                  const today = dayjs();

                  if (
                    !dayjs(startDateTime).isAfter(today, "day") ||
                    !dayjs(endDateTime).isAfter(today, "day")
                  ) {
                    return Promise.reject(new Error("活動時間需大於今天"));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              placeholder={["YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm"]}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </Form.Item>
          <Form.Item label="活動標籤" name="tags">
            <Select
              mode="tags"
              style={{
                width: "100%",
              }}
              className="text-left [&_.ant-select-selection-item]:bg-Deep-Blue [&_.ant-select-selection-item]:text-white"
              placeholder="輸入按下Enter新增標籤"
              options={tagsOptions}
            />
          </Form.Item>
          <Form.Item label="相關連結" name="socialLinkTitle">
            <Input placeholder="顯示名稱：如官網、官方粉絲團" />
          </Form.Item>
          <Form.Item name={["socialLinks", "url"]}>
            <Input placeholder="https:www.google.com.tw/" />
          </Form.Item>
          <Form.Item
            label="活動地點"
            name="country"
            dependencies={addressFieldNames}
            rules={[
              requireIfAnyAddressFieldFilled("活動地點"),
            ]}
          >
            <Select
              options={countryOptions}
              placeholder="國家"
              className="text-left"
            />
          </Form.Item>
          <Form.Item
            name="city"
            dependencies={addressFieldNames}
            rules={[
              requireIfAnyAddressFieldFilled("活動地點"),
            ]}
          >
            <Select
              options={cityOptions}
              placeholder="城市"
              className="text-left w-full"
            />
          </Form.Item>
          <Form.Item
            label={<p>詳細地址</p>}
            name="line"
            dependencies={addressFieldNames}
            rules={[
              requireIfAnyAddressFieldFilled("詳細地址"),
            ]}
          >
            <Input placeholder="南京東路四段100號10樓（鄉鎮市區、道路、街名、巷弄號、樓層）" />
          </Form.Item>
          <Form.Item label="Google Map Url" name="link" className="mb-0">
            <Input placeholder="https://maps.app.goo.gl/WJ32jmVBy5oLCHB1A" />
          </Form.Item>
          <Form.Item
            label={<p>補充說明</p>}
            name="remark"
            dependencies={addressFieldNames}
            rules={[
              requireIfAnyAddressFieldFilled("補充說明"),
            ]}
          >
            <Input placeholder="例如: 7-11對面.." />
          </Form.Item>
          <Form.Item>
            <Button className="w-full" htmlType="submit" type="primary">
              儲存
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      {previewMobileImage && (
        <Image
          wrapperStyle={{
            display: "none",
          }}
          preview={{
            visible: previewMobileOpen,
            onVisibleChange: (visible) => setPreviewMobileOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewMobileImage(""),
          }}
          src={previewMobileImage}
        />
      )}
      {previewImage && (
        <Image
          wrapperStyle={{
            display: "none",
          }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default Events;
